import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { SharePlanType } from '../../types/pool-plans.types';
import { VestingTask } from '../../types/vesting-tasks.types';

export type VestingTasksResponse = {
  data: VestingTask[];
  totalPages: number;
};
export const useVestingTasks = ({
  companyId,
  currentPage = 0,
  enabled = true,
  exercisable = false,
  finished,
  sharePlanType,
  stakeholderId,
  take = 100,
}: {
  companyId: string;
  currentPage?: number;
  enabled?: boolean;
  exercisable?: boolean | null;
  finished?: boolean;
  sharePlanType?: SharePlanType;
  stakeholderId: string;
  take?: number;
}) => {
  const queryClient = useQueryClient();
  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * take;
  const { data, fetchNextPage, hasNextPage, isError, isLoading } =
    useInfiniteQuery<VestingTasksResponse>({
      queryKey: [
        QueryKey.GET_VESTING_TASKS,
        {
          companyId,
          currentPage,
          exercisable,
          stakeholderId,
          take,
          skip,
          sharePlanType,
          finished,
        },
      ],
      queryFn: ({ pageParam = 0 }) =>
        ApiService.get(
          {
            endpoint: BackendRoute.COMPANIES,
            routePath: [companyId, 'stakeholders', stakeholderId, 'vesting-tasks'],
          },
          {
            queryParams: {
              take,
              skip: currentPage ? skip : pageParam,
              exercisable: exercisable === null ? undefined : exercisable,
              planType: sharePlanType,
              finished,
            },
          },
        ),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.totalPages > allPages.length ? allPages.length * take : undefined,
      enabled: enabled && !!companyId && !!stakeholderId,
      refetchOnWindowFocus: false,
    });
  const vestingTaskList = useMemo(
    () =>
      data?.pages?.reduce<VestingTask[]>((acc, page) => {
        return [...acc, ...page.data];
      }, []),
    [data?.pages],
  );
  const invalidateQuery = () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VESTING_TASKS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VESTING_GRAPH] });
  };

  return {
    invalidateQuery,
    vestingTasks: vestingTaskList || [],
    fetchNextPage,
    totalPages: data?.pages[0]?.totalPages || 0,
    hasNextPage,
    isError,
    isLoading,
  };
};
